import { get, create, deleteResource, updatePatch } from "./apiService";

// Notification categories for filtering
export const NOTIFICATION_CATEGORIES = {
  ALL: "all",
  GENERAL: "general",
  FUNDS: "funds",
  TRADING: "trading",
  FIAT: "fiat",
  PAY: "pay",
  PRICE_ALERT: "price_alert",
  AUTOSTACK: "autostack",
  TRANSACTION: "transaction",
};

// Notification type mappings for display
export const NOTIFICATION_TYPES = {
  // Funds
  funds_sent: { label: "Funds Sent", icon: "send", category: "funds" },
  funds_received: {
    label: "Funds Received",
    icon: "receive",
    category: "funds",
  },
  // Trading
  purchase_confirmed: { label: "Purchase", icon: "buy", category: "trading" },
  sale_complete: { label: "Sale", icon: "sell", category: "trading" },
  // Fiat
  deposit_successful: { label: "Deposit", icon: "deposit", category: "fiat" },
  withdrawal_successful: {
    label: "Withdrawal",
    icon: "withdraw",
    category: "fiat",
  },
  // 88Pay
  payment_sent: { label: "88Pay Sent", icon: "pay_sent", category: "pay" },
  payment_received: {
    label: "88Pay Received",
    icon: "pay_received",
    category: "pay",
  },
  // Price Alerts
  price_alert_up: {
    label: "Price Up",
    icon: "price_up",
    category: "price_alert",
  },
  price_alert_down: {
    label: "Price Down",
    icon: "price_down",
    category: "price_alert",
  },
  // Transaction
  transaction_failed: {
    label: "Failed",
    icon: "error",
    category: "transaction",
  },
  // AutoStack
  autostack_created: {
    label: "AutoStack Created",
    icon: "autostack",
    category: "autostack",
  },
  autostack_triggered: {
    label: "AutoStack Triggered",
    icon: "autostack",
    category: "autostack",
  },
  autostack_failed: {
    label: "AutoStack Failed",
    icon: "autostack_error",
    category: "autostack",
  },
  autostack_edited: {
    label: "AutoStack Edited",
    icon: "autostack",
    category: "autostack",
  },
  autostack_paused: {
    label: "AutoStack Paused",
    icon: "autostack_paused",
    category: "autostack",
  },
  autostack_resumed: {
    label: "AutoStack Resumed",
    icon: "autostack",
    category: "autostack",
  },
  autostack_reminder: {
    label: "AutoStack Reminder",
    icon: "autostack_reminder",
    category: "autostack",
  },
};

export const getNotifications = async (
  page = 1,
  limit = 20,
  category = null
) => {
  try {
    let url = `/api/v1/notifications/get-all?page=${page}&limit=${limit}`;
    if (category && category !== NOTIFICATION_CATEGORIES.ALL) {
      url += `&category=${category}`;
    }
    const results = await get(url);
    return results;
  } catch (err) {
    console.error("Error fetching notifications:", err);
    return { data: [], totalCount: 0 };
  }
};

export const deleteNotificationsAll = async () => {
  try {
    const results = await deleteResource("/api/v1/notifications/delete-all");
    return results;
  } catch (err) {
    console.error("Error deleting all notifications:", err);
  }
};

export const deleteNotificationService = async (notId) => {
  try {
    const result = await deleteResource(
      `/api/v1/notifications/delete?notificationId=${notId}`
    );
    return result;
  } catch (err) {
    const error = new Error(err.message || "Failed to delete notification");
    error.response = err.response;
    throw error;
  }
};

export const markAsReadService = async (notificationId) => {
  try {
    const result = await updatePatch(
      `/api/v1/notifications/mark-as-read?notificationId=${notificationId}`
    );
    return result;
  } catch (err) {
    const error = new Error(err.message || "Failed to mark as read");
    error.response = err.response;
    throw error;
  }
};

export const markAllAsReadService = async () => {
  try {
    const result = await updatePatch("/api/v1/notifications/mark-all-as-read");
    return result;
  } catch (err) {
    const error = new Error(err.message || "Failed to mark all as read");
    error.response = err.response;
    throw error;
  }
};

export const registerFCMToken = async (fcmToken) => {
  try {
    const result = await create("api/v1/notifications/register-token", {
      fcmToken: fcmToken,
    });
    return result;
  } catch (err) {
    const error = new Error(err.message || "Failed to register FCM token");
    error.response = err.response;
    throw error;
  }
};

/**
 * Get the display info for a notification type
 * @param {string} type - The notification type
 * @returns {object} - Display info with label, icon, and category
 */
export const getNotificationTypeInfo = (type) => {
  const defaultInfo = {
    label: "Notification",
    icon: "default",
    category: "general",
  };
  const typeEntries = Object.entries(NOTIFICATION_TYPES);
  const found = typeEntries.find(([key]) => key === type);
  return found ? found[1] : defaultInfo;
};

/**
 * Format a notification for display
 * @param {object} notification - The notification object from API
 * @returns {object} - Formatted notification with display properties
 */
export const formatNotification = (notification) => {
  const typeInfo = getNotificationTypeInfo(notification.type);

  return {
    ...notification,
    displayLabel: typeInfo.label,
    displayIcon: typeInfo.icon,
    displayCategory: typeInfo.category,
    formattedDate: formatNotificationDate(notification.createdAt),
  };
};

/**
 * Format notification date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatNotificationDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;

  // Time constants in milliseconds
  const MS_PER_MINUTE = 60 * 1000;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;
  const MS_PER_DAY = 24 * MS_PER_HOUR;

  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

/**
 * Get unread notification count
 * @returns {Promise<number>} - Count of unread notifications
 */
export const getUnreadCount = async () => {
  try {
    const result = await getNotifications(1, 1);
    // The API should return totalUnread in the response
    return result?.data?.totalUnread || 0;
  } catch (err) {
    console.error("Error getting unread count:", err);
    return 0;
  }
};

/**
 * Handle deep link navigation from notification
 * @param {object} notification - The notification object
 * @param {function} navigate - Navigation function from router
 */
export const handleNotificationDeepLink = (notification, navigate) => {
  if (!notification.deepLink?.screen) return;

  const { screen, params } = notification.deepLink;

  switch (screen) {
    case "TransactionDetail":
      // Fallback to digital wallet (transactions shown there)
      navigate("/trading/digitalWallet");
      break;
    case "TradeDetail":
      // Fallback to digital wallet (trades shown there)
      navigate("/trading/digitalWallet");
      break;
    case "AutoStackDetail":
      if (params?.autoStackId) {
        navigate(`/autostack/${params.autoStackId}`);
      } else {
        navigate("/autostack");
      }
      break;
    case "Wallet":
      navigate("/settings/wallet");
      break;
    case "PriceAlerts":
      navigate("/settings/price-alerts");
      break;
    case "PayTransactionDetail":
      // Fallback to dashboard (no pay transaction detail page)
      navigate("/");
      break;
    default:
      console.warn("Unknown deep link screen:", screen);
      navigate("/");
  }
};
